import { createContext, ReactNode, useContext, useEffect, useState } from "react";

import { customToast } from "../../../components/notify";
import { ModeSwitchConfirmModal } from "../components/modeSwitchConfirm";

export type SalesMode = "ordering" | "exhibition";

export interface ISalesModeContext {
    mode: SalesMode;
    isOrderingMode: boolean;
    isExhibitionMode: boolean;
    toggleMode: (onConfirm?: () => void) => void;
    setMode: (mode: SalesMode) => void;
}

const STORAGE_KEY = "sales_mode";
const DEFAULT_MODE: SalesMode = "ordering";

const SalesModeContext = createContext<ISalesModeContext | undefined>(undefined);

interface SalesModeProviderProps {
    children: ReactNode;
}

export const SalesModeProvider = ({ children }: SalesModeProviderProps) => {
    const [mode, setModeState] = useState<SalesMode>(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            return stored === "ordering" || stored === "exhibition" ? stored : DEFAULT_MODE;
        } catch {
            return DEFAULT_MODE;
        }
    });
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingMode, setPendingMode] = useState<SalesMode | null>(null);
    const [onConfirmCallback, setOnConfirmCallback] = useState<(() => void) | null>(null);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, mode);
        } catch {
            customToast.error("Failed to save sales mode to localStorage");
        }
    }, [mode]);

    const setMode = (newMode: SalesMode) => {
        if (newMode === "ordering" || newMode === "exhibition") {
            setModeState(newMode);
        } else {
            customToast.error("Invalid sales mode");
        }
    };

    const toggleMode = (onConfirm?: () => void) => {
        const nextMode = mode === "ordering" ? "exhibition" : "ordering";
        setPendingMode(nextMode);
        setShowConfirmModal(true);
        if (onConfirm) {
            setOnConfirmCallback(() => onConfirm);
        }
    };

    const handleConfirm = () => {
        if (pendingMode) {
            setModeState(pendingMode);
        }
        setShowConfirmModal(false);
        setPendingMode(null);

        // 執行回調函數
        if (onConfirmCallback) {
            onConfirmCallback();
            setOnConfirmCallback(null);
        }
    };

    const handleCancel = () => {
        setShowConfirmModal(false);
        setPendingMode(null);
        setOnConfirmCallback(null);
    };

    const value: ISalesModeContext = {
        mode,
        isOrderingMode: mode === "ordering",
        isExhibitionMode: mode === "exhibition",
        toggleMode,
        setMode,
    };

    return (
        <SalesModeContext.Provider value={value}>
            {children}
            <ModeSwitchConfirmModal
                open={showConfirmModal}
                pendingMode={pendingMode}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />
        </SalesModeContext.Provider>
    );
};

export const useSalesModeContext = () => {
    const context = useContext(SalesModeContext);
    if (context === undefined) {
        throw new Error("useSalesModeContext must be used within a SalesModeProvider");
    }
    return context;
};
